import { Component, OnInit, OnDestroy, Output, EventEmitter, inject, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { FavoritePoiService, FavoritePoiItem } from '../services/favorite-poi.service';
import { TranslationService } from '../services/translation.service';
import { TranslatePipe } from '../pipes/translate.pipe';
import { PoiDetailService } from '../poi-detail.service';
import { IvionNavigationService } from '../api/ivion-navigation.service';
import { PoiSelectedEventData } from '../../PoiSelectedEventData';
import { MiddlewareApiService } from '../api/middleware-api.service';
import { ToastService } from '../services/toast.service';


@Component({
	selector: 'app-favorite-poi-overview',
	standalone: true,
	imports: [CommonModule, TranslatePipe],
	template: `
		<div class="screen-modal-container favorite-poi-container">
			<a class="screen-close" (click)="close()"></a>
			<h1>{{ 'favorite_poi_title' | translate }}</h1>

			<!-- Loading indicator -->
			<div class="favorites-loading" *ngIf="isBusy">
				<div class="spinner"></div>
				<span>{{ 'loading' | translate:'Načítání' }}</span>
			</div>

			<!-- Favorites list (empty list shown when no favorites - same as legacy) -->
			<div class="data-table" *ngIf="!isBusy">
				<div class="data-table-row" *ngFor="let item of favoritePois; trackBy: trackBySourceId">
					<!-- Icon -->
					<div class="data-table-row-item row-icon">
						<img [src]="resolvePoiIcon(item)" [alt]="item.title" />
					</div>

					<!-- Title & Description -->
					<div class="data-table-row-item row-content">
						<div class="favorite-poi-title">{{ item.title }}</div>
						<div class="favorite-poi-description">{{ item.description }}</div>
					</div>

					<!-- Show on map -->
					<div
						class="data-table-row-item row-icon clickable"
						[title]="showOnMapTitle"
						(click)="showOnMap(item)">
						<img src="assets/images/place-green.png" alt="Show on map" />
					</div>

					<!-- Remove -->
					<div
						class="data-table-row-item row-icon clickable"
						[title]="removeTitle"
						(click)="remove(item)">
						<img src="assets/images/delete.png" alt="Remove" />
					</div>
				</div>
			</div>
		</div>
	`,
	styles: [`
		.favorite-poi-container {
			color: #fff;
		}

		h1 {
			font-size: 28px;
			margin-bottom: 20px;
		}

		.favorites-loading {
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;
			padding: 50px;
		}

		.spinner {
			width: 40px;
			height: 40px;
			border: 3px solid rgba(255, 255, 255, 0.3);
			border-radius: 50%;
			border-top-color: #fff;
			animation: spin 1s ease-in-out infinite;
			margin-bottom: 10px;
		}

		@keyframes spin {
			to {
				transform: rotate(360deg);
			}
		}

		.data-table {
			display: flex;
			flex-direction: column;
			color: black;
			background: white;
			max-height: 70vh;
			overflow-y: auto;
			margin: 0.5rem;
			font-size: 1em;
			line-height: 1.5;
		}

		.data-table-row {
			display: flex;
			align-items: center;
			background: white;
			border-bottom: 1px solid #565656;
			min-height: 65px;
			flex-shrink: 0;
		}

		.data-table-row-item {
			display: flex;
			align-items: center;
			color: #333;
			border-right: 1px solid #ccc;
			padding: 0.5em;
		}

		.data-table-row-item:last-child {
			border-right: none;
		}

		.row-icon {
			flex: 0 0 50px;
			justify-content: center;

			img {
				width: 24px;
				height: 24px;
				object-fit: contain;
			}
		}

		.row-icon.clickable {
			cursor: pointer;
			transition: opacity 0.2s;

			&:hover {
				opacity: 0.7;
			}
		}

		.row-content {
			flex: 1;
			min-width: 0;
			flex-direction: column;
			align-items: flex-start;
		}

		.favorite-poi-title {
			color: #333;
			font-size: 1.1em;
			font-weight: bold;
			margin-bottom: 5px;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
			max-width: 100%;
		}

		.favorite-poi-description {
			font-size: 12px;
			color: #666;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
			max-width: 100%;
		}
	`]
})
export class FavoritePoiOverviewComponent implements OnInit, OnDestroy {
	@Output() closeEvent = new EventEmitter<void>();

	private favoritePoiService = inject(FavoritePoiService);
	private translationService = inject(TranslationService);
	private poiDetailService = inject(PoiDetailService);
	private ivionNavigation = inject(IvionNavigationService);
	private middlewareApi = inject(MiddlewareApiService);
	private ngZone = inject(NgZone);
	private toastService = inject(ToastService);
	private subscription: Subscription | null = null;

	favoritePois: FavoritePoiItem[] = [];
	isBusy = false;

	get showOnMapTitle(): string {
		return this.translationService.translate('poi_show_on_map_title', 'Show on map');
	}

	get removeTitle(): string {
		return this.translationService.translate('favorite_poi_remove_title', 'Remove from favorites');
	}

	ngOnInit(): void {
		// 1. IMMEDIATELY show cached data
		this.favoritePois = this.favoritePoiService.getCachedFavorites();

		// 2. Subscribe to updates (for background refresh)
		this.subscription = this.favoritePoiService.favorites$.subscribe(items => {
			this.ngZone.run(() => {
				this.favoritePois = items;
			});
		});

		// 3. Start background refresh
		this.refreshInBackground();
	}

	ngOnDestroy(): void {
		this.subscription?.unsubscribe();
	}

	trackBySourceId(_index: number, item: FavoritePoiItem): number {
		return item.sourceId;
	}

	private async refreshInBackground(): Promise<void> {
		// Show spinner only if no cached data
		if (this.favoritePois.length === 0) {
			this.isBusy = true;
		}

		await this.favoritePoiService.refreshFavoritesInBackground();

		this.isBusy = false;
	}

	showOnMap(item: FavoritePoiItem): void {
		// 1. IMMEDIATELY open panel with data from favorites list
		const poiData: PoiSelectedEventData = {
			id: item.sourceId,
			poiTypeId: item.poiTypeId,
			titles: { default: item.title },
			descriptions: { default: item.description },
			icon: 'assets/images/poi-icon.png',
			globalLocation: undefined
		};
		this.poiDetailService.emitPoiSelected(poiData);
		this.close();

		// 2. PARALLEL background: load POI, update icon, navigate (non-blocking)
		this.ivionNavigation.findPoi(item.sourceId).then(poi => {
			if (!poi) {
				console.error('[FavoritePoiOverview] POI not found:', item.sourceId);
				return;
			}

			// Update panel with correct icon from IVION
			if (poi.icon) {
				this.ngZone.run(() => {
					this.poiDetailService.emitPoiSelected({
						...poiData,
						icon: poi.icon,
						globalLocation: poi.location || undefined
					});
				});
			}

			// Navigate and highlight (previous highlights are removed by the service)
			this.ivionNavigation.navigateToPoi(poi);
		}).catch((error: unknown) => {
			console.error('[FavoritePoiOverview] Failed to navigate to POI:', error);
		});
	}

	remove(item: FavoritePoiItem): void {
		// 1. IMMEDIATELY remove from UI (optimistic update)
		const itemIndex = this.favoritePois.findIndex(p => p.sourceId === item.sourceId);
		if (itemIndex === -1) return;

		const removedItem = this.favoritePois[itemIndex];
		this.favoritePois = this.favoritePois.filter(p => p.sourceId !== item.sourceId);

		// 2. API call in background (non-blocking)
		this.favoritePoiService.removePoiFromFavorites(item.sourceId).then(result => {
			this.ngZone.run(() => {
				if (result.success) {
					// Show success toast
					const msg = this.translationService.translate('favorite_poi_remove_success_msg');
					this.toastService.success(msg);
					console.log(`[FavoritePoiOverview] Removed POI ${item.sourceId} from favorites`);
				} else {
					// Restore item on failure
					this.favoritePois = [...this.favoritePois.slice(0, itemIndex), removedItem, ...this.favoritePois.slice(itemIndex)];
					this.toastService.error(this.translationService.translate('favorite_poi_add_remove_error_msg'));
					console.error('[FavoritePoiOverview] Failed to remove favorite');
				}
			});
		}).catch((error: any) => {
			this.ngZone.run(() => {
				// Restore item on error
				this.favoritePois = [...this.favoritePois.slice(0, itemIndex), removedItem, ...this.favoritePois.slice(itemIndex)];
				this.toastService.error(this.translationService.translate('favorite_poi_add_remove_error_msg'));
				console.error('[FavoritePoiOverview] Failed to remove favorite:', error);
			});
		});
	}

	resolvePoiIcon(item: FavoritePoiItem): string {
		// FMS Building POI type ID check would require middleware settings
		// For now, use generic POI icon
		// TODO: Check against fmsBuildingPoiTypeId and fmsFunctionalAreaPoiTypeId from settings
		return 'assets/images/poi-icon.png';
	}

	close(): void {
		this.closeEvent.emit();
	}
}
